/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { postLogin } from "api/auth";
import { useAuth } from "context/AuthContext";

// @mui material components
import Card from "@mui/material/Card";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import { jwtDecode } from "jwt-decode";
import bgImage from "assets/images/cars.jpg";

function Basic() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // can be 'success', 'info', 'warning', or 'error'

  const handleOpenSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  async function handleSubmit() {
    await loginAPI();
  }

  async function loginAPI() {
    setIsSubmitting(true);
    handleOpenSnackbar("Connexion en cours...", "info");
    
    const credentials = {
      username: username,
      password: password,
    };
    
    try {
      const response = await postLogin(credentials);
      if (response.status === 200) {
        // Store the session token in local storage
        localStorage.setItem("sessionToken", response.data.token);
        
        // Decode the token to get user information
        const decoded = jwtDecode(response.data.token);
        if (!Array.isArray(decoded.user_areas)) {
          decoded.user_areas = decoded.user_area ? [decoded.user_area] : [];
        }
        setUser(decoded);
        
        handleOpenSnackbar("Connexion réussie!", "success");
        
        // Redirect to home page
        setTimeout(() => {
          navigate("/accueil");
        }, 1000);
      }
    } catch (error) {
      console.error("Error during login:", error);
      
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.error || "Erreur de connexion";
        handleOpenSnackbar(errorMessage, "error");
      } else {
        // Network error or other issue
        handleOpenSnackbar("Erreur de connexion au serveur", "error");
      }
    }
    setIsSubmitting(false);
  }

  useEffect(() => {
    // Validate form
    const isFormValid = username.trim() !== "" && password.trim() !== "";
    setDisabled(!isFormValid);
  }, [username, password]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deconnexion = params.get("deconnexion");
    // Check if session token exists
    const sessionToken = localStorage.getItem("sessionToken");

    // Check if the navigation to this page was from the navbar
    if (deconnexion === "true" || deconnexion === true) {
      if (sessionToken) {
        localStorage.removeItem("sessionToken");
      }
    } else {
      // If the user manually entered the URL, redirect to the accueil page
      if (sessionToken) {
        try {
          const decoded = jwtDecode(sessionToken);
          if (!Array.isArray(decoded.user_areas)) {
            decoded.user_areas = decoded.user_area ? [decoded.user_area] : [];
          }
          if (decoded.exp > Date.now() / 1000) {
            navigate("/accueil");
          }
        } catch (error) {
          localStorage.removeItem("sessionToken");
        }
      }
    }
  }, [navigate, location.search]);

  return (
    <BasicLayout image={bgImage}>
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={10000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", color: "white !important" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1.5}
          px={2}
          variant="gradient"
          borderRadius="lg"
          sx={{
            height: "6rem",
            background: !darkMode ? "linear-gradient(195deg, #364c70, #273956)" : "white",
            boxShadow: darkMode ? "0 1px 4px 1px white" : "0 1px 4px 1px #344767",
          }}
        >
          <MDTypography
            variant="h3"
            style={{
              width: "100%",
              textAlign: "center",
              lineHeight: "4.5rem",
            }}
            color={darkMode ? "inherit" : "white"}
          >
            Connexion
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="text"
                autoComplete="username"
                onChange={(event) => setUsername(event.target.value)}
                label="Nom d'utilisateur"
                fullWidth
                error={username.trim() === "" && username !== ""}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
                label="Mot de passe"
                fullWidth
                error={password.trim() === "" && password !== ""}
              />
            </MDBox>
            <MDBox mt={4} mb={1} sx={{ justifyContent: "center", width: "100%", display: "flex" }}>
              <MDButton
                disabled={disabled || isSubmitting}
                onClick={handleSubmit}
                variant="gradient"
                color="dark"
                size="large"
                fullWidth
                sx={{
                  background: !darkMode ? "linear-gradient(195deg, #364c70, #273956)" : "white",
                  boxShadow: darkMode ? "0 1px 4px 1px white" : "0 1px 4px 1px #344767",
                }}
              >
                Se connecter
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
